import {
  memo,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useAppContext } from "../Context/AppContext";
import { clearTokens } from "../auth/authStorage";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FaLock } from "react-icons/fa";
import {
  FiBriefcase,
  FiCalendar,
  FiEdit,
  FiHome,
  FiLogOut,
  FiMail,
  FiMessageSquare,
  FiRss,
  FiUser,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import {
  LuPanelLeftClose,
  LuPanelLeftOpen,
  LuPanelRightClose,
  LuPanelRightOpen,
} from "react-icons/lu";
import { isBusinessOrExecutiveMembership } from "../utils/membershipTier";
import { ADMIN_NAV_ITEMS, type AdminNavKey } from "../constants/adminNav";

const SIDEBAR_TRANSITION_MS = 500;
const SIDEBAR_OPEN_PX = 280;
const SIDEBAR_CLOSED_PX = 48;
const ICON_SLOT_PX = 48;

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  closeSidebarOnMobile?: () => void;
  /** Unpaid explorer: lock member nav, keep toggle / Events / logout usable. */
  membershipLocked?: boolean;
}

function Sidebar({
  isOpen,
  toggleSidebar,
  closeSidebarOnMobile,
  membershipLocked = false,
}: SidebarProps) {
  const { t, i18n } = useTranslation();
  const { user, viewMode } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const isRtl = i18n.dir() === "rtl";
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const handleLogout = useCallback(async () => {
    clearTokens();
    navigate("/", { replace: true });
    closeSidebarOnMobile?.();
  }, [navigate, closeSidebarOnMobile]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      closeSidebarOnMobile?.();
    },
    [navigate, closeSidebarOnMobile],
  );

  const handleAdminNavigate = useCallback(
    (tab: AdminNavKey) => {
      navigate("/admin", { state: { adminTab: tab } });
      closeSidebarOnMobile?.();
    },
    [navigate, closeSidebarOnMobile],
  );

  const isActive = useCallback(
    (path: string) => {
      const current = location.pathname.replace(/\/+$/, "") || "/";
      const target = path.replace(/\/+$/, "") || "/";
      if (target === "/event-booking") {
        return (
          current === "/event-booking" ||
          current.startsWith("/event-booking/") ||
          current.startsWith("/event-questions") ||
          current.startsWith("/confirm-booking")
        );
      }
      if (target === "/members-benefits-smbf") {
        return (
          current === "/members-benefits-smbf" ||
          current.startsWith("/members-benefits-smbf/") ||
          current === "/business-membership" ||
          current === "/executive-membership" ||
          current === "/youth-membership" ||
          current === "/early-stage-membership"
        );
      }
      return current === target || current.startsWith(`${target}/`);
    },
    [location.pathname],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  const isAdmin = useMemo(
    () => user?.user?.isAdmin === 1,
    [user?.user?.isAdmin],
  );
  const showAdminMenu = isAdmin && viewMode === "admin";

  const showMuslimInvest = useMemo(
    () => isBusinessOrExecutiveMembership(user?.user?.membershipName),
    [user?.user?.membershipName],
  );

  const menuItems = useMemo(
    (): Array<{ path: string; label: string; Icon: IconType }> => [
      { path: "/updates", label: t("sidebar.updates"), Icon: FiRss },
      { path: "/dashboard", label: t("sidebar.dashboard"), Icon: FiHome },
      { path: "/event-booking", label: t("sidebar.events"), Icon: FiCalendar },
      ...(showMuslimInvest
        ? [
            {
              path: "/investing",
              label: t("sidebar.muslimInvest"),
              Icon: FiBriefcase,
            },
          ]
        : []),
      { path: "/partners", label: t("sidebar.partners"), Icon: FiUserPlus },
      { path: "/messages", label: t("sidebar.messages"), Icon: FiMessageSquare },
      {
        path: "/connections",
        label: t("sidebar.connections"),
        Icon: FiUsers,
      },
      { path: "/attendee-notes", label: t("sidebar.attendeeNotes"), Icon: FiEdit },
      { path: "/contact-us", label: t("sidebar.contactUs"), Icon: FiMail },
      { path: "/my-account", label: t("sidebar.myAccount"), Icon: FiUser },
    ],
    [t, showMuslimInvest],
  );

  const currentAdminTab = useMemo((): AdminNavKey => {
    if (location.pathname.startsWith("/admin/event")) {
      return "events";
    }
    const fromState = (location.state as { adminTab?: AdminNavKey } | null)
      ?.adminTab;
    if (fromState && ADMIN_NAV_ITEMS.some((item) => item.key === fromState)) {
      return fromState;
    }
    return "events";
  }, [location.pathname, location.state]);

  const linkClass = (active: boolean, hovered: boolean) =>
    [
      "nav-link",
      "text-white",
      "d-flex",
      "align-items-center",
      "justify-content-start",
      "border-0",
      "py-2",
      "pe-3",
      "ps-0",
      "rounded-0",
      "text-nowrap",
      "text-start",
      "gap-0",
      active || hovered ? "bg-project-transparent" : "bg-transparent",
    ]
      .filter(Boolean)
      .join(" ");

  const hoverBind = (key: string) => ({
    onMouseEnter: () => setHoveredKey(key),
    onMouseLeave: () => setHoveredKey(null),
  });

  const linkStyle: CSSProperties = { width: SIDEBAR_OPEN_PX };

  const SidebarToggleIcon = isRtl
    ? isOpen
      ? LuPanelRightClose
      : LuPanelRightOpen
    : isOpen
      ? LuPanelLeftClose
      : LuPanelLeftOpen;

  const portalTitle = t("sidebar.portalTitle");

  /** Fade labels in/out with the rail; layout stays fixed so icons don't jump. */
  const labelStyle: CSSProperties = {
    opacity: isOpen ? 1 : 0,
    transition: `opacity ${SIDEBAR_TRANSITION_MS}ms ease`,
    whiteSpace: "nowrap",
  };

  return (
    <>
      {!isDesktop && (
        <div
          className="position-fixed start-0 w-100 bg-dark"
          style={{
            top: "calc(80px + 0.5rem)",
            zIndex: 1035,
            height: "calc(100vh - 80px - 0.5rem)",
            opacity: isOpen ? 0.45 : 0,
            transition: `opacity ${SIDEBAR_TRANSITION_MS}ms ease`,
            pointerEvents: isOpen ? "auto" : "none",
          }}
          onClick={toggleSidebar}
          aria-hidden={!isOpen}
        />
      )}
      <aside
        ref={sidebarRef}
        className={[
          "position-fixed",
          "bg-project-gradient",
          "text-white",
          "shadow",
          "overflow-hidden",
          isRtl ? "end-0" : "start-0",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          top: "calc(80px + 0.5rem)",
          zIndex: 1040,
          height: "calc(100vh - 80px - 0.5rem)",
          width: isOpen ? SIDEBAR_OPEN_PX : SIDEBAR_CLOSED_PX,
          transition: `width ${SIDEBAR_TRANSITION_MS}ms ease`,
          ...(isRtl
            ? { borderTopLeftRadius: "var(--bs-border-radius)" }
            : { borderTopRightRadius: "var(--bs-border-radius)" }),
        }}
      >
        {/*
          Fixed inner width = open width. Outer aside clips on close so icons
          never reflow / jump — labels are revealed by the width animation.
        */}
        <div
          className="d-flex flex-column h-100"
          style={{ width: SIDEBAR_OPEN_PX, minWidth: SIDEBAR_OPEN_PX }}
        >
          <div className="sticky-top border-bottom border-white border-opacity-25 bg-project-gradient">
            <div className="position-relative d-flex align-items-center px-2 py-3">
              <div
                className="d-flex align-items-center gap-2 flex-grow-1 min-w-0 overflow-hidden pe-5"
                style={labelStyle}
                aria-hidden={!isOpen}
              >
                <img
                  src="/logoOld.png"
                  alt={t("nav.logoAlt")}
                  width={40}
                  height={40}
                  className="flex-shrink-0"
                  style={{ objectFit: "contain" }}
                />
                <div className="flex-grow-1 min-w-0 overflow-hidden">
                  <div
                    className="fw-semibold text-white lh-sm"
                    style={{ whiteSpace: "normal" }}
                  >
                    {portalTitle}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-light btn-sm p-1 border border-white border-opacity-50 rounded d-inline-flex align-items-center justify-content-center position-absolute top-50 translate-middle-y"
                style={{
                  width: 32,
                  height: 32,
                  transition: `inset-inline-start ${SIDEBAR_TRANSITION_MS}ms ease`,
                  insetInlineStart: isOpen
                    ? `calc(100% - 40px)`
                    : `${(ICON_SLOT_PX - 32) / 2}px`,
                }}
                onClick={toggleSidebar}
                title={t("nav.toggleNavigation")}
                aria-label={t("nav.toggleNavigation")}
                aria-expanded={isOpen}
              >
                <SidebarToggleIcon size={18} aria-hidden />
              </button>
            </div>
          </div>

          <div className="position-relative flex-grow-1 overflow-hidden d-flex flex-column">
            <ul className="navbar-nav flex-column flex-grow-1 overflow-auto py-3 mb-0">
              {showAdminMenu
                ? ADMIN_NAV_ITEMS.map((item) => {
                    const Icon = item.Icon;
                    const label = t(item.labelKey);
                    const adminActive = !!(
                      isActive("/admin") && currentAdminTab === item.key
                    );
                    return (
                      <li key={item.key} className="nav-item">
                        <button
                          type="button"
                          className={linkClass(
                            adminActive,
                            hoveredKey === item.key,
                          )}
                          style={linkStyle}
                          aria-current={adminActive ? "page" : undefined}
                          title={label}
                          aria-label={label}
                          onClick={() => handleAdminNavigate(item.key)}
                          {...hoverBind(item.key)}
                        >
                          <span
                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: ICON_SLOT_PX,
                              minWidth: ICON_SLOT_PX,
                            }}
                          >
                            <Icon size={20} aria-hidden />
                          </span>
                          <span className="text-truncate" style={labelStyle}>
                            {label}
                          </span>
                        </button>
                      </li>
                    );
                  })
                : menuItems.map((item) => {
                    const Icon = item.Icon;
                    return (
                      <li key={item.path} className="nav-item">
                        <button
                          type="button"
                          className={linkClass(
                            isActive(item.path),
                            hoveredKey === item.path,
                          )}
                          style={linkStyle}
                          aria-current={isActive(item.path) ? "page" : undefined}
                          title={item.label}
                          aria-label={item.label}
                          onClick={() => handleNavigate(item.path)}
                          {...hoverBind(item.path)}
                        >
                          <span
                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: ICON_SLOT_PX,
                              minWidth: ICON_SLOT_PX,
                            }}
                          >
                            <Icon size={20} aria-hidden />
                          </span>
                          <span className="text-truncate" style={labelStyle}>
                            {item.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
            </ul>
            {membershipLocked && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column bg-dark bg-opacity-50"
                onClick={() => {
                  window.alert(t("layout.memberOnlyAlert"));
                  closeSidebarOnMobile?.();
                }}
              >
                <div className="flex-grow-1 position-relative">
                  <div
                    className="position-absolute top-0 start-0 h-100 d-flex align-items-center justify-content-center"
                    style={{
                      width: isOpen ? SIDEBAR_OPEN_PX : SIDEBAR_CLOSED_PX,
                    }}
                  >
                    <FaLock
                      className="text-white opacity-50"
                      size={isOpen ? 56 : 28}
                      aria-hidden
                    />
                  </div>
                </div>
                <div
                  className="d-flex flex-column"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={linkClass(
                      isActive("/members-benefits-smbf"),
                      hoveredKey === "memberships",
                    )}
                    style={linkStyle}
                    aria-current={
                      isActive("/members-benefits-smbf") ? "page" : undefined
                    }
                    title={t("layout.viewMemberships")}
                    aria-label={t("layout.viewMemberships")}
                    onClick={() => handleNavigate("/members-benefits-smbf")}
                    {...hoverBind("memberships")}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: ICON_SLOT_PX, minWidth: ICON_SLOT_PX }}
                    >
                      <FiUserPlus size={20} aria-hidden />
                    </span>
                    <span className="text-truncate" style={labelStyle}>
                      {t("layout.viewMemberships")}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={linkClass(
                      isActive("/event-booking"),
                      hoveredKey === "explorer-events",
                    )}
                    style={linkStyle}
                    aria-current={isActive("/event-booking") ? "page" : undefined}
                    title={t("layout.events")}
                    aria-label={t("layout.events")}
                    onClick={() => handleNavigate("/event-booking")}
                    {...hoverBind("explorer-events")}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: ICON_SLOT_PX, minWidth: ICON_SLOT_PX }}
                    >
                      <FiCalendar size={20} aria-hidden />
                    </span>
                    <span className="text-truncate" style={labelStyle}>
                      {t("layout.events")}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto sticky-bottom border-top border-white border-opacity-25 py-2 position-relative z-1 bg-project-gradient">
            <button
              type="button"
              className={linkClass(false, hoveredKey === "logout")}
              style={linkStyle}
              onClick={handleLogout}
              title={t("sidebar.logout")}
              aria-label={t("sidebar.logout")}
              {...hoverBind("logout")}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: ICON_SLOT_PX, minWidth: ICON_SLOT_PX }}
              >
                <FiLogOut size={20} aria-hidden />
              </span>
              <span className="text-truncate" style={labelStyle}>
                {t("sidebar.logout")}
              </span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

Sidebar.displayName = "Sidebar";

export default memo(Sidebar);
